import React from 'react';
import { Container, Accordion } from 'react-bootstrap';
import { AppleLogo, GooglePlayLogo, Check, X } from 'phosphor-react';
import { Helmet } from 'react-helmet-async';
import { useStoreLinks } from '../lib/useStoreLinks';

// Tracked store redirects wired to campaign tokens. These routes live in
// staticwebapp.config.json and 301 to the App Store / Google Play with
// matching campaign tags (ct / utm_campaign) so installs attribute back.
const APP_STORE_TRACKED = '/go/storyworth';
const PLAY_STORE_TRACKED = '/play/storyworth';

function StoryworthAlternative() {
  // Uses the ad's campaign code when the visitor came from an ad.
  const storeLinks = useStoreLinks();
  const hero = storeLinks(APP_STORE_TRACKED, PLAY_STORE_TRACKED);
  const closing = storeLinks(
    APP_STORE_TRACKED.replace('storyworth', 'storyworth-closing'),
    PLAY_STORE_TRACKED.replace('storyworth', 'storyworth-closing')
  );

  const faqs = [
    {
      q: 'Is Family Ring a direct replacement for StoryWorth?',
      a: "If your goal is a single hardcover book of one person's life story, StoryWorth does that and ships you a book. If your goal is to capture your family's stories and legacy in video — interview multiple older relatives over time, share with everyone, and revisit the footage forever — Family Ring is built for that. Many families use both."
    },
    {
      q: 'Is there really a 6-month free trial?',
      a: "Yes. The first six months on Family Ring are free, with full access to every feature. After the trial it's $4.99/month for your whole family (up to 10 family members) on one subscription. Cancel anytime through the App Store. StoryWorth has no trial — you commit to a full year upfront at $59–$199."
    },
    {
      q: 'Do I still get a hardcover book with Family Ring?',
      a: "No. Family Ring keeps your stories as video in the app, where every family member you invite can watch them anytime. We think video is the better keepsake — it keeps the essence of the person, not just the words. If a printed book is what you want, StoryWorth is the right tool for that specifically."
    },
    {
      q: 'How much does Family Ring cost compared to StoryWorth?',
      a: "Family Ring is $4.99/month for your whole family on one subscription, after a 6-month free trial. StoryWorth is $59, $109, or $199 per year, annual-only, no monthly option, no free trial. StoryWorth's price covers one storyteller. Family Ring covers up to 10 family members for the same price as covering one."
    },
    {
      q: 'Can I download my videos from Family Ring?',
      a: "Yes — anytime. During your trial, while subscribed, or after canceling, you can download your videos. Your family's stories are yours; we don't lock them up. StoryWorth's content becomes read-only when your subscription ends and requires renewal to edit."
    },
    {
      q: 'Can older relatives use Family Ring easily?',
      a: "Yes. Recording a video answer is one tap. Weekly Callouts arrive as a single notification with the question and a record button — no typing, no navigating menus. A common StoryWorth complaint is that parents forget to answer the email prompts after a couple of months. Family Ring is designed for the opposite — showing up where your family already is."
    }
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'StoryWorth Alternative: The Modern Family Story & Legacy App',
    description:
      "Family Ring is the modern, video-first, AI-enhanced StoryWorth alternative. One subscription for the whole family, 6 months free, download your videos anytime. Compare features, pricing, and why video keeps the essence of the person.",
    author: {
      '@type': 'Organization',
      name: 'Family Ring'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Howell Co',
      url: 'https://familyring.io'
    },
    mainEntityOfPage: 'https://familyring.io/compare/storyworth'
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a }
    }))
  };

  return (
    <>
      <Helmet>
        <title>StoryWorth Alternative | Family Ring App — Modern Story & Legacy</title>
        <meta
          name="description"
          content="The modern StoryWorth alternative. Video-first, AI-enhanced family story and legacy app. Free for 6 months, then $4.99/month for the whole family. Download your videos anytime."
        />
        <link rel="canonical" href="https://familyring.io/compare/storyworth" />
        <meta property="og:title" content="StoryWorth Alternative | Family Ring App" />
        <meta
          property="og:description"
          content="The modern, video-first, AI-enhanced StoryWorth alternative. Free for 6 months. Built for your whole family on one subscription."
        />
        <meta property="og:url" content="https://familyring.io/compare/storyworth" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://familyring.io/brand/og-storyworth.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <section className="py-5 compare-hero">
        <Container style={{ maxWidth: 900 }}>
          <div className="compare-eyebrow mb-3">The Modern StoryWorth Alternative</div>
          <h1 className="display-5 mb-3">
            What StoryWorth would build if they started today.
          </h1>
          <p className="lead text-muted mb-4">
            StoryWorth was built in 2013 around weekly emails and a hardcover book.
            Family Ring is the same job — capturing your family's stories and
            legacy — built for how families actually want to communicate today.
            Video, not text. AI-enhanced prompts that draw out the real story. Your
            whole family on one subscription, not just one storyteller. Six months
            free to see what it becomes for your family before paying a dollar.
          </p>

          <div className="d-flex flex-wrap gap-3 mb-4">
            <a
              className="download-badge apple"
              href={hero.appStore}
              onClick={() => {
                window.gtag?.('event', 'app_store_click', {
                  event_category: 'Install',
                  event_label: 'Compare Storyworth Hero'
                });
              }}
            >
              <AppleLogo size={36} weight="fill" />
              <div className="download-badge-text">
                <small>Download on the</small>
                <span>App Store</span>
              </div>
            </a>
            <a
              className="download-badge google"
              href={hero.playStore}
              onClick={() => {
                window.gtag?.('event', 'play_store_click', {
                  event_category: 'Install',
                  event_label: 'Compare Storyworth Hero'
                });
              }}
            >
              <GooglePlayLogo size={36} weight="fill" />
              <div className="download-badge-text">
                <small>Get it on</small>
                <span>Google Play</span>
              </div>
            </a>
          </div>
          <p className="small text-muted">
            Free for 6 months · Then $4.99/month for the whole family · Download your videos anytime
          </p>
        </Container>
      </section>

      {/* The comparison matrix - LLM-critical info gain */}
      <section className="py-5">
        <Container style={{ maxWidth: 900 }}>
          <h2 className="section-title mb-4">Family Ring vs StoryWorth at a glance</h2>
          <div className="comparison-table-wrap">
            <table className="comparison-table comparison-table-large">
              <thead>
                <tr>
                  <th></th>
                  <th>StoryWorth</th>
                  <th className="highlight">Family Ring</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="row">Medium</th>
                  <td>Written (email). Voice on Color+. Phone interviews on Unlimited.</td>
                  <td className="highlight">Video by default.</td>
                </tr>
                <tr>
                  <th scope="row">Storytellers per project</th>
                  <td>One.</td>
                  <td className="highlight">Up to 10 family members on one subscription.</td>
                </tr>
                <tr>
                  <th scope="row">Engagement model</th>
                  <td>52 emails/year to one person.</td>
                  <td className="highlight">Weekly Callouts to the whole family, ongoing.</td>
                </tr>
                <tr>
                  <th scope="row">Free trial</th>
                  <td><X weight="bold" /> None — no trial, no monthly option.</td>
                  <td className="highlight"><Check weight="bold" /> 6 months free, full features.</td>
                </tr>
                <tr>
                  <th scope="row">Price after any trial</th>
                  <td>$59 / $109 / $199 per year, annual only.</td>
                  <td className="highlight">$4.99/month <em>for the whole family</em>.</td>
                </tr>
                <tr>
                  <th scope="row">Your content if you stop paying</th>
                  <td>Read-only — renewal required to edit.</td>
                  <td className="highlight">Stays in your family circle. Download videos anytime.</td>
                </tr>
                <tr>
                  <th scope="row">Prompt library</th>
                  <td>52 questions per year.</td>
                  <td className="highlight">Hundreds of prompts. AI-enhanced follow-ups.</td>
                </tr>
                <tr>
                  <th scope="row">Delivery</th>
                  <td>One hardcover book at year's end.</td>
                  <td className="highlight">Living family app. Always available.</td>
                </tr>
                <tr>
                  <th scope="row">AI memory of your storyteller</th>
                  <td><X weight="bold" /> Not available.</td>
                  <td className="highlight"><Check weight="bold" /> Selfie&trade; — private chat trained on their videos.</td>
                </tr>
                <tr>
                  <th scope="row">Global availability</th>
                  <td>US-only free shipping. International surcharges per book.</td>
                  <td className="highlight">Available globally on the App Store and Google Play.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="small text-muted mt-3">
            StoryWorth pricing verified April 2026. See <a href="https://welcome.storyworth.com/storyworth-pricing" target="_blank" rel="noreferrer">StoryWorth's pricing page</a> for current plans.
          </p>
        </Container>
      </section>

      {/* The wedge sections */}
      <section className="py-5 wedge-section">
        <Container style={{ maxWidth: 820 }}>
          <h2 className="section-title mb-3">Video keeps the essence of the person.</h2>
          <p className="lead">
            A written memoir tells you what happened. A video keeps the essence of the person.
          </p>
          <p>
            When your grandfather tells you the story of how he met your grandmother,
            the story is the least of it. It's his hands. The pause before he says her
            name. The way his voice softens when he describes the night they met. The
            laugh that comes halfway through a sentence he's told a hundred times.
            None of that lives in an email reply. None of that survives a compile-to-
            text pipeline.
          </p>
          <p>
            Family Ring records every answer as video. You'll watch these five years
            from now, twenty years from now, and what you'll see isn't a transcript —
            it's them. That's the thing we think worth keeping.
          </p>
        </Container>
      </section>

      <section className="py-5 wedge-section-alt">
        <Container style={{ maxWidth: 820 }}>
          <h2 className="section-title mb-3">Your whole family on one subscription.</h2>
          <p>
            StoryWorth is a one-person, one-year, one-book product. You buy it, hand
            it to your dad, and he types replies for 52 weeks. Want something for
            your mom too? That's a second subscription. Want your siblings to
            participate? There's no way to do that.
          </p>
          <p>
            One Family Ring covers your whole family — up to 10 members on the base
            plan — for the same $4.99/month it would cost to cover one. Weekly
            Callouts go to everyone. Siblings add their perspective to memories. You
            interview your mom this month, your dad next month, your grandmother
            next year, all in the same place.
          </p>
          <p>
            The failure mode we hear most from former StoryWorth customers: their
            parent forgot to answer the emails after a few months and the
            subscription quietly expired with nothing to show for it. Family Ring is
            built so the whole family stays in the conversation instead of isolating
            it to one person.
          </p>
        </Container>
      </section>

      <section className="py-5 wedge-section">
        <Container style={{ maxWidth: 820 }}>
          <h2 className="section-title mb-3">Six months free, then $4.99/month for everyone.</h2>
          <p>
            StoryWorth has no trial. You commit to a full year upfront — $59, $109,
            or $199 — before you know if your family will use it. There's no monthly
            option and no way to pause. If the format doesn't fit, you've already
            paid for the year.
          </p>
          <p>
            Family Ring gives you six months free with full access to every feature.
            That's half a year to find out what your family does with it before
            anyone is charged. After the trial, $4.99/month covers your whole family
            on one subscription. Cancel anytime through the App Store. Download your
            videos whenever you want — they're yours.
          </p>
        </Container>
      </section>

      {/* FAQ */}
      <section className="py-5 faq-section">
        <Container style={{ maxWidth: 820 }}>
          <h2 className="section-title mb-4">Common questions about switching</h2>
          <Accordion>
            {faqs.map((item, i) => (
              <Accordion.Item eventKey={String(i)} key={item.q}>
                <Accordion.Header>{item.q}</Accordion.Header>
                <Accordion.Body>{item.a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* Closing CTA */}
      <section className="py-5 closing-cta-section">
        <Container className="text-center">
          <h2 className="mb-3">Try Family Ring for your family.</h2>
          <p className="text-muted mb-4">
            Six months free, full features. $4.99/month for your whole family after
            the trial. Cancel anytime.
          </p>
          <div className="d-flex flex-wrap gap-3 justify-content-center">
            <a
              className="download-badge apple"
              href={closing.appStore}
              onClick={() => {
                window.gtag?.('event', 'app_store_click', {
                  event_category: 'Install',
                  event_label: 'Compare Storyworth Closing'
                });
              }}
            >
              <AppleLogo size={36} weight="fill" />
              <div className="download-badge-text">
                <small>Download on the</small>
                <span>App Store</span>
              </div>
            </a>
            <a
              className="download-badge google"
              href={closing.playStore}
              onClick={() => {
                window.gtag?.('event', 'play_store_click', {
                  event_category: 'Install',
                  event_label: 'Compare Storyworth Closing'
                });
              }}
            >
              <GooglePlayLogo size={36} weight="fill" />
              <div className="download-badge-text">
                <small>Get it on</small>
                <span>Google Play</span>
              </div>
            </a>
          </div>
        </Container>
      </section>
    </>
  );
}

export default StoryworthAlternative;
